// HeavyJob usage hours on rental PO lines, for the Billing tab's Purchase
// Orders grid.
//
// JDE has no usage on rental cost: a 52200 row's Total_Hours is the voucher
// QUANTITY — "1" is a month of rent (54026070's 40-50' boom, PO 231561 line
// 1, posts 1.0 against 441 hours on the cards). The usage lives in HeavyJob,
// where the unit is coded PO-<po>-<line>: the JDE PO and its line number
// (F4311 PDLNID / 1000). readRentalHours.sql sums those codes per HeavyJob
// job; this folds them onto the grid's PO line keys.
//
// Matched by PO line, not job — a job-70 PO line can carry hours from a
// job-71 card (231560-1: 435 on 70, 12 on 71). A unit HeavyJob codes on a
// different PO (54026070's 135' manlift is PO 231801 in JDE but PO-230239-1
// on the cards), or a continuation line the cards never moved to, gets
// nothing — HOURS stays blank rather than guessing.

const num = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};
const unpad = (po) => String(po ?? '').trim().replace(/^0+/, '');
const r1 = (n) => Math.round(n * 10) / 10;

// The rental cost type.
export const RENTAL_CT = '52200';

/** The POs whose rental (52200) lines want HeavyJob hours, unpadded and sorted. */
export const rentalPos = (txns) => [...new Set((txns || [])
    .filter((t) => String(t.ct) === RENTAL_CT && t.po && t.pol)
    .map((t) => unpad(t.po))
    .filter((p) => /^\d+$/.test(p)))].sort();

/**
 * readRentalHours rows → { 'po|line': { code, desc, hrs, days, first, last,
 * jobs: [{ job, hrs }] } }, keyed on the unpadded PO and the line number the
 * way lineKey builds it. A unit on two HeavyJob jobs sums both; `days` is the
 * larger job's count (the same day can sit on both).
 */
export const rentalHoursMap = (data) => {
    const m = {};
    (data || []).forEach((r) => {
        const hit = /^PO-(\d+)-(\d+)$/i.exec(String(r.Equipment_Code || '').trim());
        if (!hit) return;
        const k = `${unpad(hit[1])}|${num(hit[2])}`;
        const e = m[k] || (m[k] = { code: String(r.Equipment_Code).trim().toUpperCase(), desc: '', hrs: 0, days: 0, first: '', last: '', jobs: [] });
        const hrs = num(r.Hours);
        e.hrs = r1(e.hrs + hrs);
        e.days = Math.max(e.days, num(r.Days));
        if (!e.desc && r.Equipment_Description) e.desc = String(r.Equipment_Description).trim();
        const f = String(r.First_Date || '').slice(0, 10);
        const l = String(r.Last_Date || '').slice(0, 10);
        if (f && (!e.first || f < e.first)) e.first = f;
        if (l && l > e.last) e.last = l;
        if (hrs) e.jobs.push({ job: String(r.Job_Code || '').trim(), hrs: r1(hrs) });
    });
    Object.values(m).forEach((e) => e.jobs.sort((a, b) => b.hrs - a.hrs));
    return m;
};

/** The map key for a grid PO line (poGroupsOf's g.po + ln.pol). */
export const lineKey = (po, pol) => `${unpad(po)}|${num(pol)}`;

const md = (d) => (d ? `${num(d.slice(5, 7))}/${num(d.slice(8, 10))}` : '');

/** Hover text for a line's HOURS cell. */
export const rentalHoursTip = (e, ownJob) => {
    if (!e) return undefined;
    const span = e.first && e.last ? (e.first === e.last ? md(e.first) : `${md(e.first)}–${md(e.last)}`) : '';
    const parts = [
        `HeavyJob hours on ${e.code}${e.desc ? ` (${e.desc})` : ''}`,
        [e.days ? `${e.days} day${e.days === 1 ? '' : 's'}` : '', span].filter(Boolean).join(', '),
    ];
    const other = e.jobs.filter((j) => j.job && j.job !== String(ownJob || ''));
    if (other.length) parts.push(e.jobs.map((j) => `${j.hrs.toLocaleString('en-US')} on ${j.job}`).join(' · '));
    parts.push('JDE only carries the rental quantity');
    return parts.filter(Boolean).join('\n');
};
